<template>
  <main
      class="fixed inset-0 z-50 overflow-auto flex flex-col font-poppins bg-slate-100 text-slate-900
             dark:bg-gradient-to-r dark:from-gray-900 dark:via-black dark:to-gray-900 dark:text-white"
  >
    <header class="bg-white dark:bg-slate-900 shadow-md border-b border-slate-200 dark:border-slate-700">
      <div class="container mx-auto p-4">
        <span class="font-bold text-xl">BetterConnect</span>
      </div>
    </header>

    <section class="flex-grow grid place-items-center p-4 sm:p-6">
      <div
          class="w-full max-w-lg bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm shadow-xl rounded-2xl p-6 sm:p-8
                 border border-slate-200/50 dark:border-slate-700/50"
      >
        <span class="inline-flex items-center gap-2 px-3 py-1.5 mb-5 rounded-lg text-sm font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="9" stroke-width="2"/>
            <path d="M5.6 5.6l12.8 12.8" stroke-width="2" stroke-linecap="round"/>
          </svg>
          {{ info.banned_until ? 'Temporarily suspended' : 'Permanently banned' }}
        </span>

        <h1 class="text-2xl sm:text-3xl font-bold mb-6 [text-wrap:balance]">
          {{ info.title || (info.banned_until ? 'Your account has been suspended' : 'Your account has been banned') }}
        </h1>

        <dl class="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden bg-slate-50/50 dark:bg-slate-800/30 mb-6">
          <div class="px-4 py-4">
            <dt class="text-sm font-semibold text-slate-600 dark:text-slate-400 mb-1">Reason</dt>
            <dd class="text-slate-800 dark:text-slate-100 whitespace-pre-wrap break-words">{{ info.reason || 'Your account has been disabled.' }}</dd>
          </div>
          <div v-if="info.banned_until" class="px-4 py-4 border-t border-slate-200 dark:border-slate-700">
            <dt class="text-sm font-semibold text-slate-600 dark:text-slate-400 mb-1">Access returns</dt>
            <dd class="flex flex-wrap items-baseline gap-x-2 text-slate-800 dark:text-slate-100">
              <span class="font-semibold">{{ prettyDate(info.banned_until) }}</span>
              <span v-if="relativeUntil" class="text-sm text-slate-500 dark:text-slate-400">({{ relativeUntil }})</span>
            </dd>
          </div>
          <div v-else class="px-4 py-4 border-t border-slate-200 dark:border-slate-700">
            <dt class="text-sm font-semibold text-slate-600 dark:text-slate-400 mb-1">Duration</dt>
            <dd class="font-semibold text-slate-800 dark:text-slate-100">Permanent</dd>
          </div>
        </dl>

        <div class="flex flex-col sm:flex-row gap-3">
          <a v-if="info.appeal_url" :href="info.appeal_url"
             class="inline-flex justify-center px-5 py-2.5 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 transition-colors duration-200">
            Appeal this decision
          </a>
          <a v-else :href="mailtoHref"
             class="inline-flex justify-center px-5 py-2.5 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 transition-colors duration-200">
            Contact support
          </a>
          <button @click="logout"
                  class="inline-flex justify-center px-5 py-2.5 rounded-lg font-medium text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-200">
            Sign out
          </button>
        </div>

        <p class="mt-6 text-sm text-slate-600 dark:text-slate-400">
          Think this is a mistake? Email
          <a :href="mailtoHref" class="font-medium text-indigo-600 dark:text-indigo-400 hover:underline">{{ info.support_email || 'support@betterconnect.app' }}</a>.
          You can review our
          <router-link to="/terms" class="font-medium text-indigo-600 dark:text-indigo-400 hover:underline">Terms of Service</router-link>.
        </p>
      </div>
    </section>
  </main>
</template>

<script>
import { clearAuthToken, redirectToLogin as beginLogin } from '@/config/auth';
export default {
  name: 'BannedView',
  data() {
    return {
      info: {
        title: '',
        reason: '',
        banned_until: null,
        appeal_url: null,
        support_email: 'support@betterconnect.app'
      }
    }
  },
  computed: {
    mailtoHref() {
      const to = this.info.support_email || 'support@betterconnect.app';
      const subj = encodeURIComponent('BetterConnect – Account Appeal');
      const body = encodeURIComponent('Hello,\n\nI would like to appeal my account ban.\n\nThanks,');
      return `mailto:${to}?subject=${subj}&body=${body}`;
    },
    relativeUntil() {
      const ms = new Date(this.info.banned_until) - Date.now();
      if (isNaN(ms) || ms <= 0) return '';
      const hours = Math.ceil(ms / 3600000);
      if (hours < 24) return `in ${hours} hour${hours === 1 ? '' : 's'}`;
      const days = Math.round(hours / 24);
      return `in ${days} day${days === 1 ? '' : 's'}`;
    }
  },
  methods: {
    prettyDate(iso) {
      const d = new Date(iso);
      if (isNaN(d)) return iso;
      return d.toLocaleString('en-US', {
        weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit'
      });
    },
    logout() {
      clearAuthToken();
      localStorage.removeItem('banInfo');
      beginLogin({ originalUrl: window.location.origin });
    },
    tryLoadFromBt() {
      const bt = this.$route.query.bt;
      if (!bt) return false;
      try {
        const payload = JSON.parse(atob(bt.split('.')[1] || ''));
        if (payload && payload.b) {
          this.info = {...this.info, ...payload.b};
          return true;
        }
      } catch { /* ignore */
      }
      return false;
    }
  },
  created() {
    // 1) try URL token (bt) from /callback redirect
    const ok = this.tryLoadFromBt();
    if (ok) return;

    // 2) fallback: info saved by axios interceptor
    const cached = localStorage.getItem('banInfo');
    if (cached) {
      try {
        this.info = {...this.info, ...JSON.parse(cached)};
      } catch {
      }
    }
  }
}
</script>

